import { IRawPropertyMember, IRawUserRights } from "@propely/propely-typings";
import { actionValidatorJSON } from "@src/auth/actionValidatorJSON";
import { useAPIActionValidator } from "@src/data-hooks/actionValidator/actionValidator.service";
import {
  IRawContractorGroupMemberWithContractorGroupID,
  useContractorGroupMemberList,
} from "@src/data-hooks/contractor-group/contractorGroupMember.service";
import { usePropertyMembersForUser } from "@src/data-hooks/property/member/propertyMembers.service";
import { useProperties } from "@src/data-hooks/property/property.service";
import { usePropertiesActionValidator } from "@src/data-hooks/property/settings/propertyActionValidator.service";
import { getCurrentUser } from "@src/auth/getCurrentUser";
import { Role } from "@src/to-be-removed/LocalData/User/Role";
import { useUserRights } from "./userRights.service";

export type ActionValidatorIdentifier = (typeof actionValidatorJSON)[number]["Identifier"];

/**
 * New hook to check ActionValidator rights using new data fetching approach
 * @param action The action to validate
 * @param performedOn The propertyIDs and userID that the action is performed on
 * @returns Boolean of if the user has the right to perform the action
 */
export const useActionValidatorRight = (
  action: ActionValidatorIdentifier,
  performedOn: {
    propertyIDs?: string[];
    userID?: string;
    contractorGroups?: string[];
  }
): boolean => {
  if (!StaticActionValidator.ALL_ACTIONS.length) {
    StaticActionValidator.initialize(() => {});
  }
  // Boolean of if we found a property with a custom actionvalidator that denied access to performing an action
  var foundIllegal = false;
  // Boolean of if we found a property without the custom actionvalidator
  var foundEmpty = false;

  const { user, userId } = getCurrentUser();
  const { userRights } = useUserRights();
  const { properties } = useProperties(performedOn.propertyIDs ?? user?.properties ?? []);
  const { members: contractorGroupMembership } = useContractorGroupMemberList(
    (performedOn?.contractorGroups ?? []).map((group) => ({ contractorGroupID: group, memberID: userId }))
  );

  const filteredPropertyIDs: string[] = [];
  for (const property of properties) {
    if (property && !property.deleted && property.name) {
      filteredPropertyIDs.push(property.id);
    }
  }

  const { propertyMembers } = usePropertyMembersForUser(filteredPropertyIDs, userId);
  const { actionValidator, $actionValidator } = useAPIActionValidator();
  if ($actionValidator.isLoading) {
    return false;
  }

  const filteredActionValidators = Object.entries(actionValidator?.data ?? {}).filter(([propertyID]) => filteredPropertyIDs.includes(propertyID));

  for (const [, actionValidator] of filteredActionValidators) {
    let foundCustomActionValidator = false;
    let customAction = actionValidator?.actions?.[action];

    if (customAction) {
      let limitationAction = StaticActionValidator.ALL_ACTIONS.find((a) => a.identifier === action);

      if (limitationAction) {
        foundCustomActionValidator = true;
        if (
          canPerformLimitationAction(limitationAction, customAction.allowedGroups, {
            properties: filteredPropertyIDs,
            propertyMembers: propertyMembers ?? [],
            currentUserID: userId,
            performOnUserID: performedOn.userID,
            userRights: userRights ?? {},
            contractorGroupMembership,
          })
        ) {
          return true;
        } else {
          foundIllegal = true;
        }
      }
    }
    if (!foundCustomActionValidator) {
      foundEmpty = true;
    }
  }
  if (foundIllegal && !foundEmpty) {
    return false;
  }

  for (let limitationAction of StaticActionValidator.ALL_ACTIONS) {
    if (limitationAction.identifier === action) {
      return canPerformLimitationAction(limitationAction, undefined, {
        properties: filteredPropertyIDs,
        propertyMembers: propertyMembers ?? [],
        currentUserID: userId,
        performOnUserID: performedOn.userID,
        userRights: userRights ?? {},
        contractorGroupMembership,
      });
    }
  }
  console.log("Error: did not find action with identifier " + action);
  return false;
};

export const filterPropertiesWithActionValidatorRight = (
  action: string,
  propertyIDs: string[],
  performedOn: {
    userID?: string;
    contractorGroups?: string[];
  }
): string[] => {
  const { userId } = getCurrentUser();
  const { userRights } = useUserRights();

  const { propertyMembers } = usePropertyMembersForUser(propertyIDs, userId);
  const { actionValidators } = usePropertiesActionValidator(propertyIDs);
  const { members: contractorGroupMembership } = useContractorGroupMemberList(
    (performedOn?.contractorGroups ?? []).map((group) => ({ contractorGroupID: group, memberID: userId }))
  );

  const result: string[] = [];
  for (const property of propertyIDs) {
    const propertyMember = propertyMembers.find((member) => member.propertyID === property);
    if (!propertyMember) continue;

    let foundIllegal = false;
    let foundEmpty = false;
    let added = false;

    for (const actionValidator of actionValidators) {
      if (added) break;
      let foundCustomActionValidator = false;
      let customAction = actionValidator?.data.actions?.[action];

      if (customAction) {
        let action = StaticActionValidator.ALL_ACTIONS.find((action) => action.identifier === action.identifier);
        if (action) {
          foundCustomActionValidator = true;
          if (
            canPerformLimitationAction(action, customAction.allowedGroups, {
              properties: [property],
              propertyMembers: [propertyMember],
              currentUserID: userId,
              performOnUserID: performedOn.userID,
              userRights: userRights ?? {},
              contractorGroupMembership,
            })
          ) {
            result.push(property);
            added = true;
            break;
          } else {
            foundIllegal = true;
          }
        }
      }
      if (!foundCustomActionValidator) {
        foundEmpty = true;
      }
    }

    if (foundIllegal && !foundEmpty) {
      continue;
    }

    for (let action of StaticActionValidator.ALL_ACTIONS) {
      if (action.identifier === action.identifier) {
        if (
          canPerformLimitationAction(action, undefined, {
            properties: [property],
            propertyMembers: [propertyMember],
            currentUserID: userId,
            performOnUserID: performedOn.userID,
            userRights: userRights ?? {},
            contractorGroupMembership,
          })
        ) {
          result.push(property);
          break;
        }
      }
    }
  }

  return result;
};

interface LimitationGroup {
  identifier: string;
  canPerformAction: (props: ActionProps) => boolean;
}

const LimitationGroup_currentUser: LimitationGroup = {
  identifier: "current_user",
  canPerformAction: ({ performOnUserID, currentUserID }) => {
    return currentUserID === performOnUserID;
  },
};

const LimitationGroup_operationManager: LimitationGroup = {
  identifier: "operation_manager",
  canPerformAction: ({ userRights, properties }) => {
    return userRights?.operationManagerProperties?.some((property) => properties.includes(property)) ?? false;
  },
};

const LimitationGroup_landlordAdmin: LimitationGroup = {
  identifier: "landlord_admin",
  canPerformAction: ({ propertyMembers }) => {
    return propertyMembers?.some((member) => member?.roles.includes(Role.LANDLORD.id) && member?.admin) ?? false;
  },
};

const LimitationGroup_landlord: LimitationGroup = {
  identifier: "landlord",
  canPerformAction: ({ propertyMembers }) => {
    return propertyMembers?.some((member) => member?.roles.includes(Role.LANDLORD.id)) ?? false;
  },
};

const LimitationGroup_tenantAdmin: LimitationGroup = {
  identifier: "tenant_admin",
  canPerformAction: ({ propertyMembers }) => {
    return propertyMembers?.some((member) => member?.roles.includes(Role.RENTER.id) && member?.admin) ?? false;
  },
};

const LimitationGroup_tenant: LimitationGroup = {
  identifier: "tenant",
  canPerformAction: ({ propertyMembers }) => {
    return propertyMembers?.some((member) => member?.roles.includes(Role.RENTER.id)) ?? false;
  },
};

const LimitationGroup_entrepreneurAdmin: LimitationGroup = {
  identifier: "entrepreneur_admin",
  canPerformAction: ({ propertyMembers, contractorGroupMembership }) => {
    return (
      (propertyMembers?.some((member) => member?.roles.includes(Role.ENTREPRENEUR.id) && member?.admin) ?? false) ||
      contractorGroupMembership.some((member) => member.admin)
    );
  },
};

const LimitationGroup_entrepreneur: LimitationGroup = {
  identifier: "entrepreneur",
  canPerformAction: ({ propertyMembers, contractorGroupMembership }) => {
    return (propertyMembers?.some((member) => member?.roles.includes(Role.ENTREPRENEUR.id)) ?? false) || contractorGroupMembership.length > 0;
  },
};

export class StaticActionValidator {
  static ALL_ACTIONS: LimitationAction[] = [];
  static ALL_LIMITATION_GROUPS: LimitationGroup[] = [
    LimitationGroup_currentUser,
    LimitationGroup_operationManager,
    LimitationGroup_landlordAdmin,
    LimitationGroup_landlord,
    LimitationGroup_tenantAdmin,
    LimitationGroup_tenant,
    LimitationGroup_entrepreneurAdmin,
    LimitationGroup_entrepreneur,
  ];

  static initialize(completion: (success: boolean) => void) {
    for (let actionGroup of actionValidatorJSON) {
      let action: LimitationAction = {
        identifier: actionGroup.Identifier,
        allowedGroups: [],
      };

      for (let limitation in actionGroup) {
        if ((actionGroup as any)[limitation].toLowerCase() === "x") {
          let group = this.getLimitationGroup(limitation);
          if (group) action.allowedGroups.push(group);
        }
      }
      this.ALL_ACTIONS.push(action);
    }
    completion(true);
  }

  private static getLimitationGroup(identifier: string) {
    for (let group of this.ALL_LIMITATION_GROUPS) {
      if (group.identifier === identifier) {
        return group;
      }
    }
    return null;
  }
}
type LimitationAction = {
  identifier: string;
  allowedGroups: LimitationGroup[];
};

const canPerformLimitationAction = (action: LimitationAction, allowedGroups: string[] | undefined, props: ActionProps) => {
  let groups: LimitationGroup[] = [];
  if (allowedGroups) {
    for (const group of allowedGroups) {
      const groupData = StaticActionValidator.ALL_LIMITATION_GROUPS.find((data) => data.identifier === group);
      if (groupData) groups.push(groupData);
    }
  } else {
    groups = action.allowedGroups;
  }

  for (let group of groups) {
    if (group.canPerformAction(props)) {
      return true;
    }
  }
  return false;
};

type ActionProps = {
  properties: string[];
  propertyMembers: IRawPropertyMember[];
  userRights: IRawUserRights;
  currentUserID: string;
  performOnUserID: string | undefined;
  contractorGroupMembership: IRawContractorGroupMemberWithContractorGroupID[];
};
